import { Link } from "react-router";
import VideoEmbed from "../../design-system/components/Video/Video.jsx";
import { workshopStartStyles } from "../admin/workshops/workshops-styles.css.js";
import { SanityImg } from "../../lib/images.jsx";
import { TagRow } from "./TagRow.jsx";
import BlockContent from "../../design-system/components/BlockContent/BlockContent";
import Button from "../../design-system/components/Button/Button.jsx";
import libraryStyles from "./library.css.ts";
import cx from "../../lib/cx.js";
import { Variants } from "../../design-system/components/Button/Variants.jsx";
import { getPrettyLinkParts } from "../../lib/link-utils.js";
import { LinkPreview } from "../../design-system/components/LinkPreview/LinkPreview.jsx";
import { shrink } from "../../lib/shrink";

const LibraryItemPreviewContent = ({
  item,
  tagFilter,
  toggleTag,
  top,
  children,
  to,
  href,
}) => {
  let Header = top;
  if (!Header && item.imageUrl) {
    Header = (
      <SanityImg
        url={item.imageUrl}
        height={250}
        width={500}
        fit="crop"
        className={cx(
          workshopStartStyles.coverImg,
          libraryStyles.previewElement,
        )}
      />
    );
  }

  let Title = item.title;
  if (to) {
    Title = <Link to={to}>{item.title}</Link>;
  } else if (href) {
    Title = (
      <a href={href} target={"_blank"} rel="noreferrer">
        {item.title}
      </a>
    );
  }

  return (
    <div className={libraryStyles.masonryItem}>
      <div className={libraryStyles.box}>
        {Header}
        <div className={libraryStyles.boxBody}>
          <TagRow item={item} tagFilter={tagFilter} toggleTag={toggleTag} />
          <h3 className={libraryStyles.boxHeadline}>{Title}</h3>
          {children}
          {item.descriptionObj && (
            <BlockContent content={item.descriptionObj} textColor="white" />
          )}
          {to && (
            <Button variant={Variants.QUATERNARY} to={to}>
              Öffnen
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * ExternalLink is a React functional component responsible for rendering a preview of an external link.
 *
 * @component
 * @param {object} props - The props object containing properties required for the component rendering.
 * @param {object} props.item - Contains information about the content and link preview.
 * @param {object} props.item.content - Contains the link to preview.
 * @param {object} props.item.linkPreviewInfo - Contains additional preview information.
 *
 * @returns {JSX.Element} A JSX element representing the external link preview, including an image, title, and description if available.
 *
 */
const ExternalLink = (props) => {
  const { url, title, description, imgUrl } = getPrettyLinkParts({
    link: props.item.content.link,
    previewInfo: props.item.linkPreviewInfo,
  });

  const top = (
    <a href={url} title={title} target="_blank" rel="noreferrer">
      <LinkPreview imgUrl={imgUrl} alt={title || description} />
    </a>
  );

  return (
    <LibraryItemPreviewContent top={top} title={title} href={url} {...props}>
      {props.item.descriptionObj ? null : description ? (
        <p>{shrink(description, 300)}</p>
      ) : (
        <a href={url} target="_blank" rel="noreferrer">
          {url}
        </a>
      )}
    </LibraryItemPreviewContent>
  );
};

const typeToItemComp = {
  RichText: (props) => {
    const to = `${props.item.id}`;
    return <LibraryItemPreviewContent {...props} to={to} />;
  },
  Video: (props) => (
    <LibraryItemPreviewContent
      {...props}
      top={<VideoEmbed url={props.item.content.video} />}
    />
  ),
  ExternalLink: (props) => ExternalLink(props),
};

export const LibraryItemPreview = ({ data, context }) => {
  const Comp =
    typeToItemComp[data.content.__typename] || LibraryItemPreviewContent;
  return <Comp item={data} {...context} />;
};
